/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { PropsWithChildren } from "react";

import { CosmosTagProps, CosmosText, TagList, Wrap } from "@components/elements";
import type { BlogTagFragment } from "@cosmos/hygraph-schema";
import { css, cx } from "@panda/css";
import { HStack } from "@panda/jsx";

const AttributionContainerStyles: string = css({
	display: "flex",
	position: "relative",
	alignItems: "center",
	justifyContent: "center",
	objectFit: "contain",
	h: "full",
	w: "full",
	"& > img, & > video": {
		maxHeight: "full",
		maxWidth: "full",
	},
});

const attributionPosition = 16;

const TagAttributionStackStyle: string = css({
	position: "absolute",
	insetBlockEnd: `eds.dimension.${attributionPosition}`,
	insetInlineEnd: `eds.dimension.${attributionPosition}`,
	width: `calc(100% - (2 * token(spacing.eds.dimension.${attributionPosition})))`,
});

const TagListStyle: string = css({
	"&&": {
		gap: "eds.dimension.8",
		flexShrink: 0,
		justifySelf: "flex-start",
		minHeight: "eds.dimension.24",
		"& .tag": {
			zIndex: "2",
			backdropFilter: "blur(calc(75px / 2))",
			backgroundColor: "eds.material.300",
			paddingBlock: "0",
			"& svg": {
				width: "eds.dimension.16!",
				height: "eds.dimension.16!",
			},
		},
	},
});

const AttributionStyle: string = css({
	minHeight: "eds.dimension.24",
	paddingBlock: "eds.dimension.3",
	paddingInline: "eds.dimension.8",
	borderRadius: "eds.borderRadius.4",
	borderEndEndRadius: `calc(var(--asset-border-radius, 20px) - ${attributionPosition}px) !important`,
	wordWrap: "anywhere",

	marginInlineStart: "auto",
	alignItems: "center",
	textShadow: "0px 2px 4px rgba(0, 0, 0, 0.20)",

	color: "eds.text.contrast",
	bgColor: "rgba(48, 48, 52, 0.4)",
	backdropFilter: "blur(50px)",
	lineClamp: "3",
	overflow: "hidden",
	mdDown: {
		lineClamp: "2",
	},
});

export type AttributionStackProps = {
	attribution?: string | null;
	tags?: CosmosTagProps[] | BlogTagFragment[] | null;
	className?: string | null;
};
export type CosmosTagsAndAttributionProps = PropsWithChildren<
	AttributionStackProps & {
		className?: string;
	}
>;

export const AttributionStack = ({ attribution, tags, className }: AttributionStackProps): React.JSX.Element | null => {
	if (!attribution && !tags?.length) return null;
	return (
		<HStack
			className={cx("cosmos-attribution-stack", TagAttributionStackStyle, className)}
			gap="eds.dimension.16"
			justifyContent="space-between"
			alignItems="flex-end"
		>
			<TagList className={TagListStyle} tags={tags} size="uiSM" />
			<CosmosText className={cx("cosmos-attribution", AttributionStyle)} variant="uiSM" text={attribution} />
		</HStack>
	);
};

export const CosmosTagsAndAttribution = ({
	tags,
	attribution,
	className,
	children,
}: CosmosTagsAndAttributionProps): React.JSX.Element | null => {
	return (
		<Wrap
			condition={!!attribution || !!tags?.length}
			wrap={(children) => (
				<div className={cx("cosmos-attribution-wrapper", AttributionContainerStyles, className)}>
					{children}
					<AttributionStack tags={tags} attribution={attribution} />
				</div>
			)}
		>
			{children}
		</Wrap>
	);
};
